import { useAppTranslation } from '../i18n/useAppTranslation';
import SportUniverse from '../components/SportUniverse';
import { PerformanceProfile, PerformanceEvolution } from '../components/PerformanceCharts';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Container, Typography, Button, Box, Paper, Grid, Card, CardContent, Chip, 
  Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  useTheme, useMediaQuery, List, ListItem, ListItemText, ListItemAvatar, Avatar, IconButton,
  ToggleButton, ToggleButtonGroup, Collapse, Stack
} from '@mui/material';
import { 
  ExpandMore, ExpandLess,
  Event as EventIcon, 
  FitnessCenter, 
  CreditCard,
  Cancel,
  Person,
  Euro,
  Chat,
  Schedule,
  Dashboard as DashboardIcon2,
  TrendingUp,
  CalendarMonth,
  Payments,
  EventNote as EventNoteIcon2,
  ViewModule,
  ViewList,
  Email,
  PersonAdd,
  ManageAccounts,
  CloudUpload,
} from '@mui/icons-material';
import { 
  Bar, 
  BarChart,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
} from 'recharts';
import CoachList from './CoachList';
import api from '../services/api';
import { format, parseISO, isAfter } from 'date-fns';

import Sidebar, { SidebarItem } from '../components/Sidebar';
import DeleteAccountButton from '../components/DeleteAccountButton';
import ChangePassword from '../components/ChangePassword';
import SeasonProgress from '../components/SeasonProgress';
import PerformanceGauges from '../components/PerformanceGauges';
import {
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { getMediaUrl } from '../utils/imageUrl';


const SportifDashboard = () => {
  const { tr, locale, dateLocale } = useAppTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(() => {
    const saved = localStorage.getItem('sportif_tab');
    return saved !== null ? Number(saved) : 0;
  });

  useEffect(() => {
    localStorage.setItem('sportif_tab', String(tabValue));
  }, [tabValue]);

  useEffect(() => {
    const state = location.state as { tab?: number } | null;
    if (state && state.tab !== undefined) {
      setTabValue(state.tab);
      localStorage.setItem('sportif_tab', String(state.tab));
    }
  }, [location.state]);

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [viewModeBookings, setViewModeBookings] = useState<'grid' | 'list'>('grid');
  const [bookingFilter, setBookingFilter] = useState('ALL');
  const [showHistoriqueSportif, setShowHistoriqueSportif] = useState(false);
  const [myBookings, setMyBookings] = useState<any[]>([]);
  const [performances, setPerformances] = useState<any[]>([]);
  const [paymentStats, setPaymentStats] = useState({
    current_month_due: 0,
    payment_history: []
  });

  const [stats, setStats] = useState({
    upcoming: 0,
    completed: 0
  });

  // Payment Modal State
  const [inviteData, setInviteData] = useState({ email: '', name: '', custom_message: '' });
  const [invitations, setInvitations] = useState<any[]>([]);
  const [inviteStatus, setInviteStatus] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Profil sportif
  const [profileData, setProfileData] = useState({ sport: '', height: '', weight: '', goals: '', medical_info: '' });
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [profileMessage, setProfileMessage] = useState<{ type: string; text: string }>({ type: '', text: '' });

  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [selectedBookingPayment, setSelectedBookingPayment] = useState<any>(null);

  const translateStatus = (status: string) => {
    const map: Record<string, string> = {
      'PENDING': 'EN ATTENTE',
      'CONFIRMED': 'CONFIRMÉ',
      'COMPLETED': 'TERMINÉ',
      'CANCELLED': 'ANNULÉ'
    };
    return map[status] || status;
  };

  const translatePayment = (status: string) => {
    const map: Record<string, string> = {
      'PENDING': 'EN ATTENTE',
      'COMPLETED': 'PAYÉ',
      'FAILED': 'ÉCHOUÉ',
      'REFUNDED': 'REMBOURSÉ',
      'UNPAID': 'IMPAYÉ'
    };
    return map[status] || status;
  };

  const fetchMyBookings = useCallback(async () => {
    try {
      const response = await api.get('/coaching/bookings/');
      setMyBookings(response.data);
    } catch (error: any) {
      console.error("Failed to fetch bookings", error);
    }
  }, []);

  const fetchPerformances = useCallback(async () => {
    try {
      const response = await api.get('/coaching/performances/');
      setPerformances(response.data);
    } catch (error: any) {
      console.error("Failed to fetch performances", error);
    }
  }, []);

  const fetchSportifProfile = useCallback(async () => {
    try {
      const res = await api.get('/users/me/sportif_profile/');
      const p = res.data;
      setProfileData({
        sport: p.sport || '',
        height: p.height || '',
        weight: p.weight || '',
        goals: p.goals || '',
        medical_info: p.medical_info || '',
      });
      if (p.photo) setPhotoPreview(getMediaUrl(p.photo) || null);
    } catch (e) {
      console.error('Failed to fetch sportif profile', e);
    }
  }, []);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleProfileUpdate = async () => {
    try {
      const formData = new FormData();
      formData.append('sport', profileData.sport);
      if (profileData.height) formData.append('height', profileData.height);
      if (profileData.weight) formData.append('weight', profileData.weight);
      formData.append('goals', profileData.goals);
      formData.append('medical_info', profileData.medical_info);
      if (selectedPhoto) formData.append('photo', selectedPhoto);
      await api.patch('/users/me/sportif_profile/', formData);
      await refreshUser();
      setProfileMessage({ type: 'success', text: 'Profil mis à jour avec succès !' });
      setSelectedPhoto(null);
    } catch (e) {
      console.error('Profile update failed', e);
      setProfileMessage({ type: 'error', text: 'Erreur lors de la mise à jour du profil.' });
    }
  };

  const fetchInvitations = useCallback(async () => {
    try {
      const res = await api.get('/coaching/invitations/');
      setInvitations(res.data);
    } catch (e) {
      console.error('Failed to fetch invitations', e);
    }
  }, []);

  const handleSendInvite = async () => {
    setInviteStatus(null);
    try {
      await api.post('/coaching/invite/', { ...inviteData, mode: 'email' });
      setInviteStatus({ type: 'success', text: 'Invitation envoyée avec succès !' });
      setInviteData({ email: '', name: '', custom_message: '' });
      fetchInvitations();
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.response?.data?.detail || "Erreur lors de l'envoi de l'invitation.";
      setInviteStatus({ type: 'error', text: msg });
    }
  };

  const fetchPaymentStats = useCallback(async () => {
    try {
      const response = await api.get('/payments/payments/dashboard_stats/');
      setPaymentStats(response.data);
    } catch (error: any) {
      console.error("Failed to fetch payment stats", error);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchMyBookings(), fetchPerformances(), fetchPaymentStats()]);
  }, [fetchMyBookings, fetchPerformances, fetchPaymentStats]);

  const handleConfirmPresence = useCallback(async (bookingId: number) => {
    try {
      await api.post(`/coaching/bookings/${bookingId}/sportif_accept/`);
      await fetchMyBookings();
    } catch (error: any) {
      console.error('Failed to confirm presence', error);
    }
  }, [fetchMyBookings]);

  useEffect(() => {
    // Charger uniquement les données critiques au démarrage (onglet actif)
    fetchMyBookings();
    fetchPaymentStats();
    // Différer les données non critiques après le rendu initial
    const timer = setTimeout(() => {
      fetchPerformances();
      fetchInvitations();
      fetchSportifProfile();
    }, 300);

    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshAll();
    };
    document.addEventListener('visibilitychange', onVisible);
    const poll = setInterval(() => { refreshAll(); }, 30000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(poll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rafraîchir les données pertinentes à chaque changement d'onglet
  useEffect(() => {
    if (tabValue === 0 || tabValue === 2) { fetchMyBookings(); fetchPaymentStats(); }
    if (tabValue === 0 || tabValue === 3) { fetchPerformances(); }
    if (tabValue === 6) { fetchPaymentStats(); }
    if (tabValue === 5) { fetchInvitations(); }
    if (tabValue === 7) { fetchSportifProfile(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  useEffect(() => {
    const state = location.state as { tab?: number } | null;
    if (state?.tab !== undefined) {
      setTabValue(state.tab);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    const upcoming = myBookings.filter(b => isAfter(parseISO(b.start_time), new Date()) && b.status !== 'CANCELLED').length;
    const completed = myBookings.filter(b => b.status === 'COMPLETED').length;
    setStats({ upcoming, completed });
  }, [myBookings]);

  const handleCancelBooking = async (bookingId: number) => {
    try {
      await api.post(`/coaching/bookings/${bookingId}/cancel/`);
      await refreshAll();
    } catch (error) {
      console.error('Failed to cancel booking', error);
    }
  };

  const handlePayBooking = (booking: any) => {
    setSelectedBookingPayment(booking);
    setOpenPaymentModal(true);
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED': return '#1976d2';
      case 'COMPLETED': return '#2e7d32';
      case 'CANCELLED': return '#d32f2f';
      default: return '#ed6c02';
    }
  };

  // Prepare chart data
  const chartData = [...performances]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map(p => {
      return {
        date: p.date,
        endurance: (p as any).endurance || 0,
        // Alias « Physique » = condition physique (endurance) : le graphique
        // « Performance & Score Global » l'utilise sous ce nom.
        physical_condition: (p as any).endurance || 0,
        technical_mastery: p.technical_mastery || 0,
        mental_strength: p.mental_strength || 0,
        motivation: p.motivation || 0,
        fatigue_level: p.fatigue_level || 0,
        stress_level: p.stress_level || 0,
        coordination: (p as any).coordination || 0,
        explosivite: (p as any).explosivite || 0,
        tactique: p.tactique || 0,
        volontaire: p.volontaire || 0,
        // Score global stocke sur /100 -> ramene sur /10 pour les graphiques.
        global_score: (p.global_score || 0) / 10
      };
    });

  // Dernière évaluation : le backend trie déjà par -date, -created_at
  // On retrie localement pour garantir la cohérence même si l'ordre API change
  const latestPerformance = performances.length > 0
    ? [...performances].sort((a, b) => {
        const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
        if (dateDiff !== 0) return dateDiff;
        const createdDiff = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        if (createdDiff !== 0) return createdDiff;
        return (b.id || 0) - (a.id || 0);
      })[0]
    : undefined;
  const radarData = latestPerformance ? [
    { subject: 'Endurance', A: (latestPerformance as any).endurance || 0, fullMark: 10 },
    { subject: 'Technique', A: latestPerformance.technical_mastery || 0, fullMark: 10 },
    { subject: 'Mental', A: latestPerformance.mental_strength || 0, fullMark: 10 },
    { subject: 'En forme', A: latestPerformance.fatigue_level || 0, fullMark: 10 },
    { subject: 'Gestion Émotion', A: latestPerformance.stress_level || 0, fullMark: 10 },
    { subject: 'Coordination', A: (latestPerformance as any).coordination || 0, fullMark: 10 },
    { subject: 'Motivation', A: latestPerformance.motivation || 0, fullMark: 10 },
    { subject: 'Explosivité', A: (latestPerformance as any).explosivite || 0, fullMark: 10 },
    { subject: 'Tactique', A: latestPerformance.tactique || 0, fullMark: 10 },
    { subject: 'Volonté', A: latestPerformance.volontaire || 0, fullMark: 10 },
  ] : [];

  // Computed dashboard stats
  const totalPaid = (paymentStats.payment_history as any[]).filter(p => p.status === 'COMPLETED').reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const currentMonthPayments = (paymentStats.payment_history as any[]).filter(p => {
    const d = new Date(p.created_at);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  }).reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
  const upcomingBookings = myBookings.filter(b => isAfter(parseISO(b.start_time), new Date()) && b.status !== 'CANCELLED');
  const nextBooking = upcomingBookings.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())[0];

  const sidebarItems: SidebarItem[] = [
    { label: 'Tableau de bord', icon: <DashboardIcon2 fontSize="small" />, index: 0 },
    { label: 'Trouver un Coach', icon: <SearchIcon fontSize="small" />, index: 1 },
    { label: 'Mes Réservations', icon: <ScheduleIcon fontSize="small" />, index: 2 },
    { label: 'Performances', icon: <AssessmentIcon fontSize="small" />, index: 3 },
    { label: 'Messages', icon: <Chat fontSize="small" />, index: -1, navigateTo: '/messages', navigateState: { roleMode: 'SPORTIF' } },
    { label: 'Invitations', icon: <PersonAdd fontSize="small" />, index: 5 },
    { label: 'Paiements', icon: <ReceiptIcon fontSize="small" />, index: 6 },
    { label: 'Mon Profil', icon: <ManageAccounts fontSize="small" />, index: 7 },
  ];

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh' }}>
      <Sidebar
        items={sidebarItems}
        activeIndex={tabValue}
        onSelect={(idx) => { setTabValue(idx); }}
        user={user}
        onLogout={logout}
        role="SPORTIF"
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      <Box sx={{ flexGrow: 1, overflow: 'auto', minWidth: 0 }}>
        {/* Mobile header */}
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200, paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
            <IconButton aria-label={tr("Ouvrir le menu")} size="small" onClick={() => setMobileSidebarOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Box component="img" src="/g1s-logo-3d.png" sx={{ height: 32 }} />
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#FFD700' }}>G1Sport</Typography>
          </Box>
        )}
      <Container maxWidth="xl" sx={{ mt: isMobile ? 'calc(64px + env(safe-area-inset-top))' : 4, mb: 4, px: isMobile ? 1 : 3 }}>
        <SportUniverse sport={profileData.sport} compact />
        {tabValue !== 6 && tabValue !== 7 && (
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', mb: 4, gap: 1 }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ fontWeight: 800, color: 'text.primary' }}>
              {tr(sidebarItems.find(i => i.index === tabValue)?.label || '')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
               {tr("Prêt à t'entraîner,")} {user?.username} ?
            </Typography>
          </Box>
        </Box>
        )}

        {/* Stats Row - masqué sur l'onglet Tableau de bord, Coach et Paiements */}
        {tabValue !== 0 && tabValue !== 1 && tabValue !== 2 && tabValue !== 3 && tabValue !== 5 && tabValue !== 6 && tabValue !== 7 && <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Box sx={{ p: 2, borderRadius: 3, bgcolor: 'primary.main', color: 'white', mr: 2 }}>
                  <EventIcon fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{stats.upcoming}</Typography>
                  <Typography variant="body2" color="text.secondary">{tr("Séances à venir")}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Box sx={{ p: 2, borderRadius: 3, bgcolor: 'success.main', color: 'white', mr: 2 }}>
                  <FitnessCenter fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{stats.completed}</Typography>
                  <Typography variant="body2" color="text.secondary">{tr("Séances terminées")}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Box sx={{ p: 2, borderRadius: 3, bgcolor: 'warning.main', color: 'white', mr: 2 }}>
                  <Euro fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{paymentStats.current_month_due} €</Typography>
                  <Typography variant="body2" color="text.secondary">{tr("À payer (ce mois)")}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>}

        <Box>
            {/* Tableau de bord */}
            {tabValue === 0 && (
              <Box>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {[
                    { icon: <FitnessCenter sx={{ color: 'success.main', mb: 0.5 }} />, value: stats.completed, label: tr("Séances réalisées"), color: 'success.main', border: '#2e7d32' },
                    { icon: <CalendarMonth sx={{ color: 'primary.main', mb: 0.5 }} />, value: stats.upcoming, label: tr("Séances à venir"), color: 'primary.main', border: '#1976d2' },
                    { icon: <EventNoteIcon2 sx={{ color: 'secondary.main', mb: 0.5 }} />, value: myBookings.filter(b => b.status !== 'CANCELLED').length, label: tr("Réservations totales"), color: 'secondary.main', border: '#9c27b0' },
                    { icon: <TrendingUp sx={{ color: 'warning.main', mb: 0.5 }} />, value: `${totalPaid.toFixed(0)} €`, label: tr("Total payé"), color: 'warning.main', border: '#ed6c02' },
                    { icon: <Payments sx={{ color: 'error.main', mb: 0.5 }} />, value: `${paymentStats.current_month_due} €`, label: tr("À payer (ce mois)"), color: 'error.main', border: '#d32f2f' },
                    { icon: <Euro sx={{ color: '#0288d1', mb: 0.5 }} />, value: `${currentMonthPayments.toFixed(0)} €`, label: tr("Payé ce mois"), color: '#0288d1', border: '#0288d1' },
                  ].map(({ icon, value, label, color, border }) => (
                    <Grid size={{ xs: 6, sm: 4, md: 2 }} key={tr(label)} sx={{ display: 'flex' }}>
                      <Box sx={{ p: '2px', borderRadius: '20px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', width: '100%' }}>
                        <Box sx={{ p: 2, borderRadius: '18px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.3, height: '100%', bgcolor: 'background.paper' }}>
                          <Box sx={{ fontSize: 24, lineHeight: 1, mb: 0.3, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }}>{icon}</Box>
                          <Typography variant="h5" fontWeight={900} sx={{ color, lineHeight: 1.1, textShadow: 'none' }}>{value}</Typography>
                          <Typography variant="caption" sx={{ color: '#9CA3AF', lineHeight: 1.2, fontSize: '0.7rem' }}>{tr(label)}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Prochaine séance */}
                {nextBooking && (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 2, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
                    <CalendarMonth color="primary" sx={{ fontSize: 40 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>{tr("PROCHAINE SÉANCE")}</Typography>
                      <Typography variant="h6" fontWeight={800}>
                        {format(parseISO(nextBooking.start_time), 'EEEE d MMMM yyyy HH:mm', { locale: dateLocale })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                         {tr("Avec")} {nextBooking.coach_details?.username} — {nextBooking.program_details?.title || tr("Séance personnalisée")}
                      </Typography>
                    </Box>
                    <Chip label={nextBooking.status === 'CONFIRMED' ? tr("Confirmé") : tr("En attente")} color={nextBooking.status === 'CONFIRMED' ? 'success' : 'warning'} size="small" sx={{ fontWeight: 700 }} />
                  </Paper>
                )}

                {/* Séances à venir — toutes */}
                {(() => {
                  const upcoming = myBookings
                    .filter(b => b.status !== 'CANCELLED' && isAfter(parseISO(b.start_time), new Date()))
                    .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
                  if (upcoming.length === 0) return null;
                  return (
                    <>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{tr("Séances à venir")} <Chip label={upcoming.length} size="small" sx={{ ml: 1 }} /></Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                        {upcoming.map(b => {
                          const dotColor = b.status === 'CONFIRMED' ? '#3B82F6' : '#F59E0B';
                          return (
                            <Box key={b.id} sx={{
                              display: 'flex', alignItems: 'center', gap: 2,
                              px: 2, py: 1.2, borderRadius: '999px',
                              bgcolor: 'action.hover',
                              transition: 'background 0.15s',
                              '&:hover': { bgcolor: 'action.selected' },
                            }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
                              <Box sx={{ minWidth: 130, flexShrink: 0 }}>
                                <Typography variant="body2" fontWeight={800} noWrap>{b.coach_details?.username}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>{b.program_details?.title || tr("Séance libre")}</Typography>
                              </Box>
                              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                <Schedule sx={{ fontSize: 13 }} />
                                <Typography variant="caption">{format(parseISO(b.start_time), 'dd MMM yyyy HH:mm', { locale: dateLocale })}</Typography>
                              </Box>
                              {b.status === 'PENDING' && b.is_coach_created ? (
                                <Chip
                                  label="✔ Confirmer"
                                  size="small"
                                  color="success"
                                  onClick={() => handleConfirmPresence(b.id)}
                                  sx={{ fontWeight: 700, flexShrink: 0, cursor: 'pointer' }}
                                />
                              ) : (
                                <Chip
                                  label={b.status === 'CONFIRMED' ? tr("Confirmé") : tr("En attente")}
                                  size="small"
                                  color={b.status === 'CONFIRMED' ? 'primary' : 'warning'}
                                  variant="outlined"
                                  sx={{ fontWeight: 700, flexShrink: 0 }}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  );
                })()}

                {/* Activité récente — 4 dernières séances passées */}
                {(() => {
                  const past = myBookings
                    .filter(b => !isAfter(parseISO(b.start_time), new Date()) || b.status === 'CANCELLED')
                    .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
                    .slice(0, 4);
                  if (past.length === 0) return null;
                  return (
                    <>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{tr("Activité récente")}</Typography>
                      <Grid container spacing={2}>
                        {past.map(b => (
                          <Grid size={{ xs: 12, sm: 6 }} key={b.id}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body2" fontWeight={700}>{b.coach_details?.username}</Typography>
                                <Typography variant="caption" color="text.secondary">{format(parseISO(b.start_time), 'dd MMM yyyy HH:mm', { locale: dateLocale })}</Typography>
                              </Box>
                              <Chip
                                label={{ COMPLETED: tr("Terminé"), CONFIRMED: tr("Confirmé"), PENDING: tr("En attente"), CANCELLED: tr("Annulé") }[b.status as string] || b.status}
                                size="small"
                                color={b.status === 'COMPLETED' ? 'success' : b.status === 'CONFIRMED' ? 'primary' : b.status === 'CANCELLED' ? 'error' : 'warning'}
                                variant="outlined"
                              />
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  );
                })()}
                {myBookings.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">{tr("Aucune réservation pour l'instant.")}</Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => setTabValue(1)}>{tr("Trouver un Coach")}</Button>
                  </Box>
                )}

                {/* Graphiques performances */}
                {performances.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{tr("Mes Performances")}</Typography>
                    <Grid container spacing={3}>
                      {/* Radar — Dernière Analyse */}
                      <Grid size={{ xs: 12, md: 5 }}>
                        <PerformanceProfile data={radarData} date={latestPerformance?.date} />
                      </Grid>

                      {/* Composé — Performance & Score Global */}
                      <Grid size={{ xs: 12, md: 7 }}>
                        <PerformanceEvolution data={chartData} series={["endurance","technical_mastery","mental_strength","global_score"]} wellbeing={false} />
                      </Grid>

                      {/* Jauges Score de la dernière séance + Score moyen des séances, côte à côte (aussi présentes dans l'onglet Performances). */}
                      <Grid size={{ xs: 12 }}>
                        <PerformanceGauges latestPerformance={latestPerformance} performances={performances} isMobile={isMobile} />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            )}

            {tabValue === 1 && <CoachList />}

            {tabValue === 2 && (
              <Box>
                {/* KPIs */}
                <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 3 }} alignItems="stretch">
                  {[
                    { label: 'Total', value: myBookings.filter(b => b.status !== 'CANCELLED').length, color: '#6366F1', border: '#6366F1', emoji: '📋', filterKey: 'ALL' },
                    { label: tr("Confirmées"), value: myBookings.filter(b => b.status === 'CONFIRMED').length, color: '#1D4ED8', border: '#3B82F6', emoji: '🔵', filterKey: 'CONFIRMED' },
                    { label: tr("Terminées"), value: myBookings.filter(b => b.status === 'COMPLETED').length, color: '#065F46', border: '#22C55E', emoji: '✅', filterKey: 'COMPLETED' },
                    { label: tr("Annulées"), value: myBookings.filter(b => b.status === 'CANCELLED').length, color: '#991B1B', border: '#EF4444', emoji: '❌', filterKey: 'CANCELLED' },
                    { label: tr("Séances à venir"), value: stats.upcoming, color: '#0369A1', border: '#0EA5E9', emoji: '📅', filterKey: null },
                    { label: tr("À payer (ce mois)"), value: `${paymentStats.current_month_due} €`, color: '#B45309', border: '#F59E0B', emoji: '💰', filterKey: null },
                  ].map(({ label, value, color, border, emoji, filterKey }) => (
                    <Grid size={{ xs: 6, sm: 4, md: 2 }} key={tr(label)} sx={{ display: 'flex' }}>
                      <Box onClick={() => filterKey && setBookingFilter(filterKey)} sx={{ p: '2px', borderRadius: '20px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', cursor: filterKey ? 'pointer' : 'default', width: '100%', transition: 'all 0.15s', '&:hover': filterKey ? { boxShadow: `0 4px 28px ${border}60`, transform: 'translateY(-2px)' } : {} }}>
                        <Box sx={{ p: 2, borderRadius: '18px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.3, height: '100%', bgcolor: 'background.paper' }}>
                          <Typography sx={{ fontSize: 24, lineHeight: 1, mb: 0.3 }}>{emoji}</Typography>
                          <Typography variant="h5" fontWeight={900} sx={{ color, lineHeight: 1.1, textShadow: 'none' }}>{value}</Typography>
                          <Typography variant="caption" sx={{ color: '#9CA3AF', lineHeight: 1.2, fontSize: '0.7rem' }}>{tr(label)}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Filtres + toggle vue */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[{ key: 'ALL', label: tr("Tous"), color: 'primary.main' }, { key: 'PENDING', label: tr("En attente"), color: '#F59E0B' }, { key: 'CONFIRMED', label: tr("Confirmé"), color: '#3B82F6' }, { key: 'COMPLETED', label: tr("Terminé"), color: '#22C55E' }, { key: 'CANCELLED', label: tr("Annulé"), color: '#EF4444' }].map(({ key, label, color }) => (
                      <Box
                        key={key}
                        onClick={() => setBookingFilter(key)}
                        sx={{
                          px: 2, py: 0.6, borderRadius: 5, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: 0.8, userSelect: 'none',
                          border: '1px solid',
                          borderColor: bookingFilter === key ? color : 'divider',
                          bgcolor: bookingFilter === key ? (key === 'ALL' ? 'primary.main' : 'transparent') : 'transparent',
                          color: bookingFilter === key ? (key === 'ALL' ? 'white' : color) : 'text.secondary',
                          transition: 'all 0.15s',
                          '&:hover': { borderColor: color, color: color },
                        }}
                      >
                        {key !== 'ALL' && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />}
                        {tr(label)}
                      </Box>
                    ))}
                  </Box>
                  <ToggleButtonGroup value={viewModeBookings} exclusive onChange={(_, v) => v && setViewModeBookings(v)} size="small">
                    <ToggleButton value="grid" title={tr("Vue grille")}><ViewModule fontSize="small" /></ToggleButton>
                    <ToggleButton value="list" title={tr("Vue liste")}><ViewList fontSize="small" /></ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {(() => { const filtered = myBookings.filter(b => bookingFilter === 'ALL' || b.status === bookingFilter); return filtered; })().length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary">{tr("Aucune réservation trouvée.")}</Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => setTabValue(1)}>{tr("Trouver un Coach")}</Button>
                  </Box>
                ) : viewModeBookings === 'list' ? (
                  /* ── VUE LISTE réservations ── */
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {myBookings.filter(b => bookingFilter === 'ALL' || b.status === bookingFilter).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((booking) => (
                      <Paper key={booking.id} variant="outlined" sx={{ borderRadius: 2, borderLeft: `3px solid ${getStatusColor(booking.status)}`, overflow: 'hidden', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, flexWrap: 'wrap' }}>
                          {/* Coach + séance */}
                          <Box sx={{ minWidth: 0, flex: '1 1 120px' }}>
                            <Typography variant="body2" fontWeight={800} noWrap>{booking.coach_details.username}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>{booking.program_details?.title || tr("Séance libre")}</Typography>
                          </Box>
                          {/* Date */}
                          <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Schedule sx={{ fontSize: 12 }} />
                              {format(parseISO(booking.start_time), 'dd MMM yy HH:mm', { locale: dateLocale })}
                            </Typography>
                          </Box>
                          {/* Chips + actions */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, flexWrap: 'wrap' }}>
                            <Chip label={tr(translatePayment(booking.latest_payment_status || 'UNPAID'))} size="small" sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, bgcolor: booking.latest_payment_status === 'COMPLETED' ? 'success.light' : 'warning.light', color: booking.latest_payment_status === 'COMPLETED' ? 'success.dark' : 'warning.dark' }} />
                            <Chip label={tr(translateStatus(booking.status))} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: getStatusColor(booking.status), color: 'white', fontWeight: 700 }} />
                            <IconButton aria-label={tr("Ouvrir la conversation")} size="small" onClick={() => navigate('/messages', { state: { recipient: booking.coach_details, roleMode: 'SPORTIF' } })} color="primary" sx={{ p: 0.5 }}>
                              <Chat sx={{ fontSize: 16 }} />
                            </IconButton>
                            {booking.status === 'PENDING' && booking.is_coach_created && (
                              <Chip
                                label="✔ Confirmer"
                                size="small"
                                color="success"
                                onClick={() => handleConfirmPresence(booking.id)}
                                sx={{ fontWeight: 700, cursor: 'pointer', fontSize: '0.65rem', height: 20 }}
                              />
                            )}
                            {booking.status === 'PENDING' && (
                              <IconButton aria-label={tr("Annuler")} size="small" color="error" onClick={() => handleCancelBooking(booking.id)} sx={{ p: 0.5 }}>
                                <Cancel sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  /* ── VUE GRILLE réservations ── */
                  <Grid container spacing={isMobile ? 1 : 2}>
                    {myBookings.filter(b => bookingFilter === 'ALL' || b.status === bookingFilter).sort((a,b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((booking) => (
                      <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={booking.id}>
                        <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', borderTop: `3px solid ${getStatusColor(booking.status)}` }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {/* Header */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                <Person sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                                <Typography variant="body2" fontWeight={800} noWrap>{booking.coach_details.username}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                <Chip label={tr(translateStatus(booking.status))} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: getStatusColor(booking.status), color: 'white', fontWeight: 700 }} />
                                <IconButton aria-label={tr("Ouvrir la conversation")} size="small" onClick={() => navigate('/messages', { state: { recipient: booking.coach_details, roleMode: 'SPORTIF' } })} sx={{ p: 0.25 }} color="primary">
                                  <Chat sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Box>
                            </Box>
                            <Divider />
                            {/* Infos */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                              <Schedule sx={{ fontSize: 14, flexShrink: 0 }} />
                              <Typography variant="caption">{format(parseISO(booking.start_time), 'dd MMM yyyy HH:mm', { locale: dateLocale })}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                              <FitnessCenter sx={{ fontSize: 14, flexShrink: 0 }} />
                              <Typography variant="caption" noWrap>{booking.program_details?.title || tr("Séance personnalisée")}</Typography>
                            </Box>
                            {/* Paiement */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.65rem' }}>{tr("PAIEMENT")}</Typography>
                              <Typography variant="caption" sx={{ color: booking.latest_payment_status === 'COMPLETED' ? 'success.main' : 'warning.main', fontWeight: 800, fontSize: '0.7rem' }}>
                                {tr(translatePayment(booking.latest_payment_status || 'UNPAID'))}
                              </Typography>
                            </Box>
                            {/* Action confirmer présence (séances directes) + annuler */}
                            {booking.status === 'PENDING' && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.25 }}>
                                {booking.is_coach_created ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    onClick={() => handleConfirmPresence(booking.id)}
                                    sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.3, px: 1.2, borderRadius: 2 }}
                                  >
                                     {tr("✔ Confirmer ma présence")} </Button>
                                ) : <Box />}
                                <IconButton aria-label={tr("Annuler")} size="small" color="error" onClick={() => handleCancelBooking(booking.id)} sx={{ p: 0.5 }}>
                                  <Cancel sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {tabValue === 3 && (
              <Box>
                {/* ── KPIs Performances ── */}
                <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 3 }}>
                  {(() => {
                    const lp = latestPerformance as any;
                    const fmt = (v: any) => (v != null && v !== 0) ? `${v}/10` : '—';
                    return [
                      { emoji: '📊', value: performances.length, label: 'Évaluations', color: '#6366F1', border: '#6366F1' },
                      { emoji: '🏆', value: lp ? `${(lp.global_score / 10).toFixed(1).replace('.', ',')}/10` : '—', label: 'Score actuel', color: '#F59E0B', border: '#F59E0B' },
                      { emoji: '💪', value: lp ? fmt(lp.endurance) : '—', label: 'Endurance', color: '#22C55E', border: '#22C55E' },
                      { emoji: '🧠', value: lp ? fmt(lp.mental_strength) : '—', label: 'Mental', color: '#EF4444', border: '#EF4444' },
                      { emoji: '⚡', value: lp ? fmt(lp.technical_mastery) : '—', label: 'Technique', color: '#FF9800', border: '#FF9800' },
                      { emoji: '🎯', value: lp ? fmt((lp as any).coordination) : '—', label: 'Coordination', color: '#0288d1', border: '#0288d1' },
                    ];
                  })().map(({ emoji, value, label, color, border }) => (
                    <Grid size={{ xs: 6, sm: 4, md: 2 }} key={tr(label)} sx={{ display: 'flex' }}>
                      <Box sx={{ p: '2px', borderRadius: '20px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', width: '100%' }}>
                        <Box sx={{ p: 2, borderRadius: '18px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.3, height: '100%', bgcolor: 'background.paper' }}>
                          <Typography sx={{ fontSize: 24, lineHeight: 1, mb: 0.3 }}>{emoji}</Typography>
                          <Typography variant="h5" fontWeight={900} sx={{ color, lineHeight: 1.1, textShadow: 'none' }}>{value}</Typography>
                          <Typography variant="caption" sx={{ color: '#9CA3AF', lineHeight: 1.2, fontSize: '0.7rem' }}>{tr(label)}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {performances.length > 0 ? (
                  <>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{tr("Graphiques")}</Typography>
                  <Grid container spacing={4} sx={{ mb: 4 }}>
                    {/* Radar Chart - Latest Performance Analysis */}
                    <Grid size={{ xs: 12, md: 5 }}>
                      <PerformanceProfile data={radarData} date={latestPerformance?.date} />
                    </Grid>

                    {/* Evolution Chart - Performance */}
                    <Grid size={{ xs: 12, md: 7 }}>
                      <PerformanceEvolution data={chartData} series={["endurance","technical_mastery","mental_strength","global_score"]} wellbeing={false} />
                    </Grid>

                    {/* Wellbeing Chart */}
                    <Grid size={{ xs: 12 }}>
                      <PerformanceEvolution data={chartData} series={["motivation","coordination","stress_level","fatigue_level","explosivite","tactique","volontaire"]} wellbeing={true} />
                    </Grid>

                    {/* ── Jauge de forme + Barres comparatives ── */}
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Paper variant="outlined" sx={{ p: isMobile ? 1.5 : 3, borderRadius: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{tr("Score de la dernière séance")}</Typography>
                        {latestPerformance && (() => {
                          const score = latestPerformance.global_score || 0;
                          const pct = Math.round(score);
                          const color = pct >= 70 ? '#22C55E' : pct >= 45 ? '#F59E0B' : '#EF4444';
                          const label = pct >= 70 ? 'Excellent' : pct >= 45 ? 'Correct' : 'À améliorer';
                          const r = 80; const circ = 2 * Math.PI * r;
                          const dash = (pct / 100) * circ;
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                              <Box sx={{ position: 'relative', width: 200, height: 200 }}>
                                <svg width="200" height="200" viewBox="0 0 200 200">
                                  <circle cx="100" cy="100" r={r} fill="none" stroke="#e5e7eb" strokeWidth="16" />
                                  <circle cx="100" cy="100" r={r} fill="none" stroke={color} strokeWidth="16"
                                    strokeDasharray={`${dash} ${circ - dash}`}
                                    strokeLinecap="round"
                                    transform="rotate(-90 100 100)"
                                    style={{ transition: 'stroke-dasharray 0.6s ease' }}
                                  />
                                </svg>
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                                  <Typography variant="h3" fontWeight={900} sx={{ color, lineHeight: 1 }}>{(score / 10).toFixed(1).replace('.', ',')}</Typography>
                                  <Typography variant="caption" color="text.secondary">/10</Typography>
                                </Box>
                              </Box>
                              <Chip label={tr(label)} sx={{ bgcolor: color, color: 'white', fontWeight: 700, fontSize: '0.9rem', px: 1 }} />
                              <Typography variant="caption" color="text.secondary">
                                 {tr("Séance du")} {format(parseISO(latestPerformance.date), 'dd MMMM yyyy', { locale: dateLocale })}
                              </Typography>
                            </Box>
                          );
                        })()}
                      </Paper>
                      {/* ── Score moyen des séances : moyenne de toutes les séances notées ── */}
                      <Paper variant="outlined" sx={{ p: isMobile ? 1.5 : 3, borderRadius: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280, mt: 2 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{tr("Score moyen des séances")}</Typography>
                        {performances.length > 0 ? (() => {
                          const score = performances.reduce((s, p) => s + (p.global_score || 0), 0) / performances.length;
                          const pct = Math.round(score);
                          const color = pct >= 70 ? '#22C55E' : pct >= 45 ? '#F59E0B' : '#EF4444';
                          const label = pct >= 70 ? 'Excellent' : pct >= 45 ? 'Correct' : 'À améliorer';
                          const r = 80; const circ = 2 * Math.PI * r;
                          const dash = (pct / 100) * circ;
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                              <Box sx={{ position: 'relative', width: 200, height: 200 }}>
                                <svg width="200" height="200" viewBox="0 0 200 200">
                                  <circle cx="100" cy="100" r={r} fill="none" stroke="#e5e7eb" strokeWidth="16" />
                                  <circle cx="100" cy="100" r={r} fill="none" stroke={color} strokeWidth="16"
                                    strokeDasharray={`${dash} ${circ - dash}`}
                                    strokeLinecap="round"
                                    transform="rotate(-90 100 100)"
                                    style={{ transition: 'stroke-dasharray 0.6s ease' }}
                                  />
                                </svg>
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                                  <Typography variant="h3" fontWeight={900} sx={{ color, lineHeight: 1 }}>{(score / 10).toFixed(1).replace('.', ',')}</Typography>
                                  <Typography variant="caption" color="text.secondary">/10</Typography>
                                </Box>
                              </Box>
                              <Chip label={tr(label)} sx={{ bgcolor: color, color: 'white', fontWeight: 700, fontSize: '0.9rem', px: 1 }} />
                              <Typography variant="caption" color="text.secondary">
                                 {tr("Moyenne sur")} {performances.length}  {tr("séance")}{performances.length > 1 ? 's' : ''}  {tr("notée")}{performances.length > 1 ? 's' : ''}
                              </Typography>
                            </Box>
                          );
                        })() : (
                          <Typography variant="body2" color="text.secondary">{tr("Aucune séance notée pour le moment.")}</Typography>
                        )}
                      </Paper>
                    </Grid>

                    {/* ── Barres comparatives : dernière séance vs moyenne ── */}
                    <Grid size={{ xs: 12, md: 7 }}>
                      <Paper variant="outlined" sx={{ p: isMobile ? 1.5 : 3, borderRadius: 4, height: isMobile ? 320 : 380 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{tr("Dernière Séance vs Moyenne")}</Typography>
                        {(() => {
                          const axes = [
                            { key: 'endurance', label: 'Endurance' },
                            { key: 'technical_mastery', label: 'Technique' },
                            { key: 'mental_strength', label: 'Mental' },
                            { key: 'fatigue_level', label: 'En forme' },
                            { key: 'stress_level', label: 'G. Émotion' },
                            { key: 'coordination', label: 'Coordination' },
                            { key: 'motivation', label: 'Motivation' },
                            { key: 'explosivite', label: 'Explosivité' },
                            { key: 'tactique', label: 'Tactique' },
                            { key: 'volontaire', label: 'Volonté' },
                          ];
                          const n = performances.length;
                          const compareData = axes.map(({ key, label }) => ({
                            name: label,
                            Dernière: latestPerformance ? ((latestPerformance as any)[key] || 0) : 0,
                            Moyenne: n > 0 ? Math.round(performances.reduce((s: number, p: any) => s + (p[key] || 0), 0) / n * 10) / 10 : 0,
                          }));
                          return (
                            <ResponsiveContainer width="100%" height="90%">
                              <BarChart data={compareData} margin={{ top: 5, right: 10, left: isMobile ? -25 : 0, bottom: isMobile ? 40 : 20 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                                <XAxis dataKey="name" tick={{ fontSize: isMobile ? 9 : 11, fill: theme.palette.text.secondary }} angle={isMobile ? -35 : 0} textAnchor={isMobile ? 'end' : 'middle'} interval={0} />
                                <YAxis domain={[0, 10]} axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                                <RechartsTooltip contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                                <Legend wrapperStyle={{ paddingTop: 8 }} />
                                <Bar dataKey="Dernière" name="Dernière séance" fill="#D9B76A" radius={[4,4,0,0]} barSize={isMobile ? 8 : 14} />
                                <Bar dataKey="Moyenne" name="Moyenne de toutes les séances" fill="#62B5E5" radius={[4,4,0,0]} barSize={isMobile ? 8 : 14} />
                              </BarChart>
                            </ResponsiveContainer>
                          );
                        })()}
                      </Paper>
                      {/* Progression depuis le début de saison (sous « Dernière Séance vs Moyenne », à côté de « Score moyen des séances »). */}
                      {chartData.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <SeasonProgress debut={chartData[0]} now={chartData[chartData.length - 1]} />
                        </Box>
                      )}
                    </Grid>

                  </Grid>
                  </>
                ) : (
                   <Box sx={{ mb: 4, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="text.secondary">{tr("Pas assez de données pour afficher les graphiques.")}</Typography>
                   </Box>
                )}
                
                {/* ── Historique Détaillé (repliable) ── */}
                <Box
                  onClick={() => setShowHistoriqueSportif(v => !v)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer', px: 2, py: 1.2, borderRadius: '999px',
                    bgcolor: 'action.hover', mb: showHistoriqueSportif ? 2 : 0,
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight={700}>{tr("Historique Détaillé")}</Typography>
                    <Chip label={performances.length} size="small" />
                  </Box>
                  {showHistoriqueSportif ? <ExpandLess /> : <ExpandMore />}
                </Box>
                <Collapse in={showHistoriqueSportif}>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                {performances.length === 0 ? (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                       <Typography variant="h6" color="text.secondary">{tr("Aucune performance enregistrée.")}</Typography>
                    </Box>
                  </Grid>
                ) : (
                  performances
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((perf) => {
                      const coachColors = ['#6366F1','#22C55E','#F59E0B','#EF4444','#0288d1','#EC4899','#8B5CF6','#06B6D4'];
                      const coachColor = coachColors[(perf.coach_name || '').split('').reduce((s: number, c: string) => s + c.charCodeAt(0), 0) % coachColors.length];
                      return (
                    <Grid size={{ xs: 12, md: 6 }} key={perf.id}>
                      <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: `4px solid ${coachColor}` }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                             <Box>
                               <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                  {format(parseISO(perf.date), 'PPP', { locale: dateLocale })}
                               </Typography>
                               <Chip
                                 size="small"
                                 label={`👤 ${perf.coach_name}`}
                                 sx={{ mt: 0.5, fontSize: '0.7rem', height: 20, bgcolor: `${coachColor}22`, color: coachColor, border: `1px solid ${coachColor}`, fontWeight: 700 }}
                               />
                             </Box>
                             <Chip 
                               label={`${(perf.global_score / 10).toFixed(1).replace('.', ',')}/10`} 
                               color="primary"
                               sx={{ fontWeight: 700 }}
                             />
                          </Box>
                          
                          <Grid container spacing={1} sx={{ mb: 2 }}>
                            {([
                              ['Endurance',      perf.endurance,          'success.main'],
                              [tr("Technique"),      perf.technical_mastery,  'warning.main'],
                              [tr("Mental"),         perf.mental_strength,    'error.main'],
                              ['Motivation',     perf.motivation,         'primary.main'],
                              ['En forme',       perf.fatigue_level,      '#795548'],
                              [tr("Gestion Émotion"), perf.stress_level,      '#f44336'],
                              ['Coordination',   perf.coordination,       '#2196f3'],
                              [tr("Explosivité"),    perf.explosivite,        '#06B6D4'],
                              ['Tactique',       perf.tactique,           '#8B5CF6'],
                              [tr("Volonté"),        perf.volontaire,         '#F59E0B'],
                            ] as [string, any, string][]).map(([label, val, color]) => (
                              <Grid size={{ xs: 4, sm: 3 }} key={tr(label)}>
                                <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: 'background.default', borderRadius: 1 }}>
                                  <Typography variant="caption" display="block" color="text.secondary">{tr(label)}</Typography>
                                  <Typography variant="body2" fontWeight={700} sx={{ color }}>{val ?? '-'}</Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>

                          {perf.notes && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                "{perf.notes}"
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                  })
                )}
              </Grid>
              </Collapse>
              </Box>
            )}

            {tabValue === 5 && (
              <Paper sx={{ borderRadius: 4, overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" fontWeight={700}>{tr("Invitations")}</Typography>
                </Box>
                <Grid container spacing={3} sx={{ p: 3 }}>
                  {/* Formulaire invitation */}
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Stack spacing={2}>
                      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>{tr("Inviter un coach ou un sportif")}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{tr("L’envoi d’invitations par e-mail est réservé aux coachs et aux administrateurs. Pour inviter un ami, partagez-lui le lien de G1Sport.")}</Typography>
                        {inviteStatus && (
                          <Alert severity={inviteStatus.type} sx={{ mb: 2 }} onClose={() => setInviteStatus(null)}>{tr(inviteStatus.text)}</Alert>
                        )}
                        <Stack component="fieldset" disabled={user?.role !== 'ADMIN'} spacing={2} sx={{ border: 0, p: 0, m: 0 }}>
                          <TextField label={tr("Nom complet (Optionnel)")} fullWidth value={inviteData.name} onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })} />
                          <TextField label="Adresse Email" fullWidth required type="email" value={inviteData.email} onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })} />
                          <TextField label={tr("Message personnalisé (Optionnel)")} fullWidth multiline rows={3} value={inviteData.custom_message} onChange={(e) => setInviteData({ ...inviteData, custom_message: e.target.value })} placeholder={tr("Ajoutez un petit mot personnel...")} />
                          <Button variant="contained" startIcon={<Email />} onClick={handleSendInvite} disabled={!inviteData.email || user?.role !== 'ADMIN'} fullWidth>{tr("Envoyer l'invitation")}</Button>
                        </Stack>
                      </Paper>

                      {/* Aperçu du mail */}
                      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email fontSize="small" color="action" />
                          <Typography variant="caption" fontWeight={700} color="text.secondary">{tr("APERÇU DU MAIL")}</Typography>
                        </Box>
                        <Box sx={{ p: 2.5, bgcolor: 'background.default' }}>
                          <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" display="block"><b>De :</b> no-reply@g1sport.fr</Typography>
                            <Typography variant="caption" color="text.secondary" display="block"><b>{tr("À :")}</b> {inviteData.email || 'exemple@email.com'}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block"><b>{tr("Objet :")}</b> {user?.username}  {tr("vous invite à rejoindre G1Sport 🏋️")}</Typography>
                          </Box>
                          <Box sx={{ background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)', p: 3, textAlign: 'center' }}>
                              <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, color: '#FFD700', letterSpacing: '-0.02em' }}>G1Sport</Typography>
                            </Box>
                            <Box sx={{ p: '24px 24px 8px' }}>
                              <Typography sx={{ mb: 1.5, color: '#e2e8f0', fontSize: '1rem', fontWeight: 700 }}>
                                 {tr("Bonjour")} {inviteData.name || 'Ami'} 👋
                              </Typography>
                              <Typography sx={{ mb: 2, color: '#94a3b8', fontSize: '0.875rem', lineHeight: 1.7 }}>
                                <Box component="span" sx={{ color: '#00bcd4', fontWeight: 700 }}>{user?.username}</Box>  {tr("vous invite à rejoindre")}{' '}
                                <Box component="span" sx={{ color: '#FFD700', fontWeight: 700 }}>G1Sport</Box>  {tr("— la plateforme pour suivre vos entraînements et performances sportives.")} </Typography>
                            </Box>
                            {inviteData.custom_message && (
                              <Box sx={{ mx: 3, mb: 2, p: 1.5, borderLeft: '3px solid #00bcd4', color: '#c7d2e0', fontSize: '0.875rem', fontStyle: 'italic' }}>
                                {inviteData.custom_message}
                              </Box>
                            )}
                            <Box sx={{ px: 3, pb: 3, textAlign: 'center' }}>
                              <Box sx={{ display: 'inline-block', px: 4, py: 1.5, background: 'linear-gradient(135deg,#00bcd4,#0097a7)', color: '#fff', borderRadius: '50px', fontWeight: 700, fontSize: '0.9rem', boxShadow: '0 4px 16px rgba(0,188,212,0.4)' }}>
                                 {tr("Créer mon compte")} </Box>
                            </Box>
                            <Box sx={{ background: '#0f172a', p: '16px 24px', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                              <Typography sx={{ color: '#475569', fontSize: '0.75rem' }}>© 2025 G1Sport · <Box component="span" sx={{ color: '#00bcd4' }}>g1sport.fr</Box></Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Stack>
                  </Grid>

                  {/* Historique invitations */}
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>{tr("Historique des invitations")}</Typography>
                      {invitations.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">{tr("Aucune invitation envoyée.")}</Typography>
                        </Box>
                      ) : (
                        <List disablePadding>
                          {invitations.map((inv) => (
                            <React.Fragment key={inv.id}>
                              <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                                <ListItemAvatar>
                                  <Avatar sx={{ bgcolor: 'primary.main' }}><Email /></Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={inv.name || inv.email}
                                  secondary={
                                    <React.Fragment>
                                      <Typography component="span" variant="body2" color="text.primary">{inv.email}</Typography>
                                      <br />
                                      <Typography component="span" variant="caption" color="text.secondary">
                                         {tr("Envoyé le")} {format(parseISO(inv.created_at), 'dd/MM/yyyy HH:mm', { locale: dateLocale })}
                                      </Typography>
                                      <Chip label={inv.status} size="small" color={inv.status === 'SENT' ? 'success' : 'default'} variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                                    </React.Fragment>
                                  }
                                />
                              </ListItem>
                              <Divider variant="inset" component="li" />
                            </React.Fragment>
                          ))}
                        </List>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {tabValue === 7 && (
              <Paper sx={{ borderRadius: 4, overflow: 'hidden' }}>
                <Box sx={{ p: 3, background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" fontWeight={800} color="white">{tr("Mon Profil")}</Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  {profileMessage.text && (
                    <Alert severity={profileMessage.type === 'success' ? 'success' : 'error'} sx={{ mb: 3 }} onClose={() => setProfileMessage({ type: '', text: '' })}>
                      {tr(profileMessage.text)}
                    </Alert>
                  )}
                  <Grid container spacing={4}>
                    {/* Photo */}
                    <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box sx={{ position: 'relative', mb: 2 }}>
                        <Avatar
                          src={photoPreview || undefined}
                          sx={{ width: 140, height: 140, border: '4px solid', borderColor: 'primary.main', boxShadow: 4, fontSize: '3rem' }}
                        >
                          {!photoPreview && user?.username?.charAt(0).toUpperCase()}
                        </Avatar>
                        <IconButton aria-label={tr("Téléverser une photo")}
                          color="primary"
                          component="label"
                          sx={{ position: 'absolute', bottom: 4, right: -4, bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'background.paper' } }}
                        >
                          <input hidden accept="image/*" type="file" onChange={handlePhotoChange} />
                          <CloudUpload fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="h6" fontWeight={800}>{user?.username}</Typography>
                      <Chip label={tr("Sportif")} color="primary" size="small" sx={{ mt: 0.5, fontWeight: 700 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                         {tr("Cliquez sur l'icône pour changer la photo")} </Typography>
                    </Grid>

                    {/* Infos */}
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Stack spacing={2.5}>
                        <TextField
                          label={tr("Sport pratiqué")}
                          fullWidth
                          value={profileData.sport}
                          onChange={(e) => setProfileData({ ...profileData, sport: e.target.value })}
                          placeholder="Football, Tennis, Natation..."
                        />
                        <Stack direction="row" spacing={2}>
                          <TextField
                            label="Taille (cm)"
                            type="number"
                            fullWidth
                            value={profileData.height}
                            onChange={(e) => setProfileData({ ...profileData, height: e.target.value })}
                            placeholder="175"
                          />
                          <TextField
                            label="Poids (kg)"
                            type="number"
                            fullWidth
                            value={profileData.weight}
                            onChange={(e) => setProfileData({ ...profileData, weight: e.target.value })}
                            placeholder="70"
                          />
                        </Stack>
                        <TextField
                          label="Objectifs sportifs"
                          fullWidth
                          multiline
                          rows={3}
                          value={profileData.goals}
                          onChange={(e) => setProfileData({ ...profileData, goals: e.target.value })}
                          placeholder={tr("Perdre du poids, gagner en endurance, préparer un marathon...")}
                        />
                        <TextField
                          label={tr("Informations médicales (blessures, conditions)")}
                          fullWidth
                          multiline
                          rows={2}
                          value={profileData.medical_info}
                          onChange={(e) => setProfileData({ ...profileData, medical_info: e.target.value })}
                          placeholder="Aucune blessure connue..."
                        />
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<ManageAccounts />}
                          onClick={handleProfileUpdate}
                          sx={{ borderRadius: 3, fontWeight: 700, alignSelf: 'flex-start', px: 4 }}
                        >
                           {tr("Enregistrer le profil")} </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                  <ChangePassword />
                  <DeleteAccountButton />
                </Box>
              </Paper>
            )}

            {tabValue === 6 && (
              <Box>
                {/* Histogramme mensuel des paiements - Sportif (synchronisé avec onglet Mes Réservations) */}
                {(() => {
                  const allMonths: Set<string> = new Set();
                  const completedByMonth: Record<string, number> = {};
                  const unpaidByMonth: Record<string, number> = {};
                  myBookings
                    .filter((b: any) => b.status !== 'CANCELLED')
                    .forEach((b: any) => {
                      const d = new Date(b.start_time);
                      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                      allMonths.add(key);
                      const price = Number(b.program_details?.price) || 0;
                      if (b.latest_payment_status === 'COMPLETED') {
                        completedByMonth[key] = (completedByMonth[key] || 0) + price;
                      } else {
                        unpaidByMonth[key] = (unpaidByMonth[key] || 0) + price;
                      }
                    });
                  const chartData = Array.from(allMonths)
                    .sort()
                    .slice(-12)
                    .map(key => ({
                      month: new Date(key + '-01').toLocaleDateString(locale, { month: 'short', year: '2-digit' }),
                      payé: completedByMonth[key] || 0,
                      nonPayé: unpaidByMonth[key] || 0,
                    }));
                  if (chartData.length === 0) return null;
                  const totalPaidChart = myBookings
                    .filter((b: any) => b.latest_payment_status === 'COMPLETED')
                    .reduce((s: number, b: any) => s + (Number(b.program_details?.price) || 0), 0);
                  const totalUnpaidChart = myBookings
                    .filter((b: any) => b.status !== 'CANCELLED' && b.latest_payment_status !== 'COMPLETED')
                    .reduce((s: number, b: any) => s + (Number(b.program_details?.price) || 0), 0);
                  return (
                    <Paper sx={{ borderRadius: 4, p: 3, mb: 3, background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>{tr("📊 Évolution de mes Paiements")}</Typography>
                          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>{tr("12 derniers mois · Synchronisé avec Mes Réservations")}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip label={`✅ Payé: ${totalPaidChart.toFixed(2)} €`} sx={{ bgcolor: '#16A34A', color: '#fff', fontWeight: 700, fontSize: '0.75rem' }} />
                          <Chip label={`⏳ Non payé: ${totalUnpaidChart.toFixed(2)} €`} sx={{ bgcolor: '#F97316', color: '#fff', fontWeight: 700, fontSize: '0.75rem' }} />
                        </Box>
                      </Box>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} barCategoryGap="30%">
                          <defs>
                            <linearGradient id="sportifBarGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#22C55E" stopOpacity={1} />
                              <stop offset="100%" stopColor="#16A34A" stopOpacity={0.85} />
                            </linearGradient>
                            <linearGradient id="sportifBarUnpaid" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#F97316" stopOpacity={0.95} />
                              <stop offset="100%" stopColor="#EA580C" stopOpacity={0.75} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                          <XAxis dataKey="month" tick={{ fill: '#9CA3AF', fontSize: 11 }} axisLine={false} tickLine={false} dy={8} />
                          <YAxis tick={{ fill: '#9CA3AF', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}€`} width={55} />
                          <RechartsTooltip
                            cursor={{ fill: '#ffffff08' }}
                            contentStyle={{ background: '#1e1e3a', border: '1px solid #22C55E30', borderRadius: 12, color: '#fff' }}
                            formatter={(value: any, name: any) => [`${Number(value).toFixed(2)} €`, name === 'payé' ? '✅ Payé' : '⏳ Non payé']}
                          />
                          <Legend formatter={(v: any) => <span style={{ color: '#9CA3AF', fontSize: 12 }}>{v === 'payé' ? tr("✅ Payé") : tr("⏳ Non payé")}</span>} />
                          <Bar dataKey="payé" fill="url(#sportifBarGrad)" radius={[4, 4, 0, 0]} stackId="a" />
                          <Bar dataKey="nonPayé" fill="url(#sportifBarUnpaid)" radius={[4, 4, 0, 0]} stackId="a" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  );
                })()}

                {(() => {
                  const sortedBookings = [...myBookings]
                    .filter((b: any) => b.status !== 'CANCELLED')
                    .sort((a: any, b: any) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());
                  const totalPaid = sortedBookings.filter((b: any) => b.latest_payment_status === 'COMPLETED').reduce((s: number, b: any) => s + (Number(b.program_details?.price) || 0), 0);
                  const totalUnpaid = sortedBookings.filter((b: any) => b.latest_payment_status !== 'COMPLETED').reduce((s: number, b: any) => s + (Number(b.program_details?.price) || 0), 0);
                  return !isMobile ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{tr("Historique des paiements")}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip label={`✅ Payé: ${totalPaid.toFixed(2)} €`} sx={{ bgcolor: '#16A34A', color: '#fff', fontWeight: 700, fontSize: '0.75rem' }} />
                          <Chip label={`⏳ Non payé: ${totalUnpaid.toFixed(2)} €`} sx={{ bgcolor: '#F97316', color: '#fff', fontWeight: 700, fontSize: '0.75rem' }} />
                        </Box>
                      </Box>
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                              <TableCell>{tr("Date séance")}</TableCell>
                              <TableCell>{tr("Coach")}</TableCell>
                              <TableCell>{tr("Programme")}</TableCell>
                              <TableCell>{tr("Montant")}</TableCell>
                              <TableCell>{tr("Réf. transaction")}</TableCell>
                              <TableCell>{tr("Statut")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedBookings.length > 0 ? (
                              sortedBookings.map((bk: any) => (
                                <TableRow key={bk.id}>
                                  <TableCell>{format(parseISO(bk.start_time), 'Pp', { locale: dateLocale })}</TableCell>
                                  <TableCell><strong>{bk.coach_details?.username || tr("Coach")}</strong></TableCell>
                                  <TableCell>{bk.program_details?.title || tr("Séance libre")}</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>{Number(bk.program_details?.price || 0).toFixed(2)} €</TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{bk.latest_transaction_id || '—'}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={tr(translatePayment(bk.latest_payment_status || 'UNPAID'))}
                                      size="small"
                                      color={bk.latest_payment_status === 'COMPLETED' ? 'success' : 'default'}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                   {tr("Aucune séance enregistrée.")} </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  ) : (
                    <Paper sx={{ borderRadius: 4, overflow: 'hidden' }}>
                      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{tr("Paiements")}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip label={`✅ ${totalPaid.toFixed(2)} €`} sx={{ bgcolor: '#F97316', color: '#fff', fontWeight: 700, fontSize: '0.72rem', height: 22 }} />
                          <Chip label={`⏳ ${totalUnpaid.toFixed(2)} €`} sx={{ bgcolor: '#F59E0B', color: '#fff', fontWeight: 700, fontSize: '0.72rem', height: 22 }} />
                        </Box>
                      </Box>
                      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                        {sortedBookings.length === 0 ? (
                          <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">{tr("Aucune séance enregistrée.")}</Typography>
                          </Box>
                        ) : (
                          sortedBookings.map((bk: any) => (
                            <React.Fragment key={bk.id}>
                              <ListItem alignItems="flex-start" sx={{ p: 2, '&:hover': { bgcolor: 'action.hover' } }}>
                                <ListItemAvatar>
                                  <Avatar sx={{ bgcolor: bk.latest_payment_status === 'COMPLETED' ? 'success.main' : 'warning.main', color: '#fff' }}>
                                    <Euro />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primaryTypographyProps={{ component: 'div' }}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  primary={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="subtitle1" fontWeight="700">
                                        {Number(bk.program_details?.price || 0).toFixed(2)} €
                                      </Typography>
                                      <Chip
                                        label={tr(translatePayment(bk.latest_payment_status || 'UNPAID'))}
                                        size="small"
                                        color={bk.latest_payment_status === 'COMPLETED' ? 'success' : 'default'}
                                        variant={bk.latest_payment_status === 'COMPLETED' ? 'filled' : 'outlined'}
                                        sx={{ fontWeight: 700, height: 24 }}
                                      />
                                    </Box>
                                  }
                                  secondary={
                                    <Box sx={{ mt: 0.5 }}>
                                      <Typography variant="body2" color="text.primary">
                                         {tr("Séance avec")} <strong>{bk.coach_details?.username || tr("Coach")}</strong>
                                        {bk.program_details && ` · ${bk.program_details.title}`}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {format(parseISO(bk.start_time), 'dd MMM yyyy HH:mm', { locale: dateLocale })}
                                        {bk.latest_transaction_id && ` • Réf: ${bk.latest_transaction_id}`}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </ListItem>
                              <Divider variant="inset" component="li" />
                            </React.Fragment>
                          ))
                        )}
                      </List>
                    </Paper>
                  );
                })()}
              </Box>
            )}
          </Box>

        {/* Payment Modal */}
        <Dialog open={openPaymentModal} onClose={() => setOpenPaymentModal(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCard color="primary" />
             {tr("Modalités de paiement")} </DialogTitle>
          <DialogContent dividers>
            {selectedBookingPayment && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">{tr("Commande")}</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedBookingPayment.program_details?.title || tr("Séance personnalisée")}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2">{tr("Coach")} {selectedBookingPayment.coach_details.username}</Typography>
                  <Typography variant="h6" color="primary.main" fontWeight={700}>
                    {selectedBookingPayment.program_details?.price || selectedBookingPayment.coach_details.coach_profile?.hourly_rate || 0} €
                  </Typography>
                </Box>
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 1 }}>
               {tr("Le règlement de cette séance s'effectue")} <strong>{tr("directement auprès de votre coach")}</strong>{tr(", selon les moyens de paiement qu'il accepte. Une fois le paiement reçu, il le confirmera depuis son espace et la séance apparaîtra comme réglée dans votre historique.")} </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button variant="contained" onClick={() => setOpenPaymentModal(false)}>
               {tr("J'ai compris")} </Button>
          </DialogActions>
        </Dialog>
      </Container>
      </Box>
    </Box>
  );
};

export default SportifDashboard;
